import * as React from "react"
import { cn } from "@/lib/utils"

interface ToggleGroupContextValue {
  value: string
  onValueChange: (value: string) => void
}

const ToggleGroupContext = React.createContext<ToggleGroupContextValue>({
  value: '',
  onValueChange: () => {},
})

interface ToggleGroupProps {
  type: 'single'
  value: string
  onValueChange: (value: string) => void
  children: React.ReactNode
  className?: string
}

export function ToggleGroup({
  value,
  onValueChange,
  children,
  className,
}: ToggleGroupProps) {
  return (
    <ToggleGroupContext.Provider value={{ value, onValueChange }}>
      <div className={cn("inline-flex rounded-lg border p-1 bg-gray-100", className)} role="group">
        {children}
      </div>
    </ToggleGroupContext.Provider>
  )
}

interface ToggleGroupItemProps {
  value: string
  children: React.ReactNode
  className?: string
  disabled?: boolean
}

export function ToggleGroupItem({ value, children, className, disabled }: ToggleGroupItemProps) {
  const { value: selectedValue, onValueChange } = React.useContext(ToggleGroupContext)
  const isSelected = selectedValue === value

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onValueChange(value)}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-all",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
        isSelected
          ? "bg-white text-blue-700 shadow-sm"
          : "text-gray-700 hover:bg-gray-200/50",
        className
      )}
      aria-pressed={isSelected}
    >
      {children}
    </button>
  )
}

